import PropTypes from 'prop-types';
import { IconCheck, IconInfoCircle } from '@tabler/icons-react';

import Button from '../buttons/Button';
import Spinner from '../loader/Spinner';

/**
 * Donor group cards on the onboarding Import Contacts step.
 *
 * Visual spec: option B2, "three action cards"
 * (.kiro/specs/contact-grouping/mockups.html, surface B, middle panel).
 *
 * B2 was chosen over the plain status rows because it closes its own loop:
 * "Import into this group" opens the import wizard with that reserved tag already
 * selected, which IS the dedicated-import-per-tag mechanism the feature calls for.
 * The alternative reports a gap the user then has to go fix from an unrelated
 * button.
 *
 * Advisory only — the step's Next button is gated on having any import at all, not
 * on these cards. The explicit skip line keeps that honest rather than implying a
 * requirement.
 */
const DonorGroupCards = ({ groups, filledCount, onImportInto, loading }) => {
  // "0 of 3 filled" is wrong-looking while counts are in flight, for the same
  // reason "count unavailable" was: it states a result we do not have yet.
  const awaitingCounts = groups.some((group) => group.countPending);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-lg font-semibold">Donor groups</p>
          <p className="mt-0.5 text-sm text-gray-600">
            Grouping your contacts helps us write content aimed at each audience.
          </p>
        </div>
        {!loading && !awaitingCounts && (
          <p className="text-sm text-gray-600">
            {filledCount} of {groups.length} filled
          </p>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        {groups.map(({ name, tag, count, filled, available, countPending }) => {
          // A genuine read failure only. `countPending` covers not-yet-fetched, which
          // previously fell in here and flashed "count unavailable" while loading.
          const countUnavailable = available && !countPending && count === null;
          // Orange means "empty, act on this" — not a claim we can make until the
          // count lands, so a pending card stays neutral.
          const showsEmptyState = available && !countPending && !filled;

          return (
            <div
              key={name}
              className={`rounded-lg border p-4 ${
                filled ? 'border-green-100' : 'border-orange-200 bg-orange-50'
              } ${available && !countPending ? '' : '!border-gray-100 !bg-gray-10'}`}
            >
              <div className="mb-2 flex items-center gap-1.5">
                {filled ? (
                  <IconCheck size={16} className="shrink-0 text-green-600" />
                ) : (
                  <IconInfoCircle
                    size={16}
                    className={`shrink-0 ${showsEmptyState ? 'text-orange-700' : 'text-gray-500'}`}
                  />
                )}
                <span className={`font-semibold ${showsEmptyState ? 'text-orange-700' : ''}`}>{name}</span>
              </div>

              {available ? (
                <>
                  {countPending ? (
                    <div className="flex h-[30px] items-center" aria-label={`Loading ${name} count`}>
                      <Spinner className="!mr-0 size-5" />
                    </div>
                  ) : (
                    <div className={`text-h5 font-semibold ${showsEmptyState ? 'text-orange-700' : 'text-gray-950'}`}>
                      {countUnavailable ? '—' : count.toLocaleString()}
                    </div>
                  )}

                  <p className={`mb-3 mt-0.5 text-sm ${showsEmptyState ? 'text-orange-700' : 'text-gray-600'}`}>
                    {countPending
                      ? 'checking\u2026'
                      : countUnavailable
                        ? 'count unavailable'
                        : filled
                          ? 'contacts imported'
                          : 'no contacts yet'}
                  </p>

                  {/* Height reserved so the button appearing once the count lands does
                    not shift the card. Withheld while pending because its label
                    depends on `filled` — rendering it early trades one flash for
                    another. */}
                  <div className="min-h-[34px]">
                    {!countPending && (
                      <Button
                        title={filled ? 'Add more' : 'Import into this group'}
                        color={filled ? 'default' : 'primary'}
                        size="sm"
                        className="w-full"
                        onClick={() => onImportInto(tag)}
                      />
                    )}
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-600">This group is still being set up for your account.</p>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-sm text-gray-600">
        You can also import a single CSV with contact grouping specified in the &quot;tags&quot; column or manually tag
        contacts later
      </p>
    </div>
  );
};

DonorGroupCards.propTypes = {
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      tag: PropTypes.shape({ id: PropTypes.string, name: PropTypes.string }),
      count: PropTypes.number,
      filled: PropTypes.bool.isRequired,
      available: PropTypes.bool.isRequired,
      countPending: PropTypes.bool,
    }),
  ).isRequired,
  filledCount: PropTypes.number.isRequired,
  onImportInto: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

DonorGroupCards.defaultProps = {
  loading: false,
};

export default DonorGroupCards;
